import React, { useState, useEffect, useRef } from "react";
import { View, StyleSheet, Platform, ScrollView, Animated, TouchableOpacity, Text, Dimensions } from "react-native";
import { useSelector, useDispatch } from "react-redux";
import {
  TouchPointWelcomeCard,
  TouchPointSurveyContainer,
  SpecificPeriodTouchPointAction,
  SignatureSection,
} from "../../components/TouchPoint";
import {
  setTouchPointSurveyAnswers,
  setTouchPointSurveyComments,
  setTouchPointEmployeeSignature,
  setTouchPointManagerSignature,
  setTouchPointSurveyComplete,
  submitTouchPointSurvey,
  clearTouchPointSubmissionStatus,
  resetTouchPointSurveyForm,
  resetFilters,
  setSuccessMessage,
  TouchPointSubmissionPayload,
} from "../../../../store/slices/nhoSlice";
import DLOBreadcrumb, { DLOBreadcrumbItem } from "../../../../modules/dlo/components/DLOBreadcrumb";
import { useToast } from "../../../../components/LhToastContainer";
import { useSelectedLocation } from "../../../../hooks";
import { useTheme } from "../../../../theme";
import { 
  TOUCHPOINT_DISPLAY_NAMES,
  convertProbationToMilestone,
  getTouchPointDisplayName
} from '../../../../constants';

interface SpecificPeriodTouchPointProps {
  onBackClick?: () => void;
  onBackPress?: () => void; // Added this prop from App.tsx navigation
  onHomePress?: () => void; // Added for home navigation
}
const isMobileIos = Platform.OS === "ios" && Dimensions.get("window").width < 768;
const SpecificPeriodTouchPoint: React.FC<SpecificPeriodTouchPointProps> = ({
  onBackClick,
  onBackPress,
  onHomePress,
}) => {
  // Use onBackPress if provided (from App.tsx), otherwise use onBackClick (from internal navigation)
  const handleBackClick = onBackPress || onBackClick;
  const { colors, resolvedTheme } = useTheme();
  const isDarkTheme = resolvedTheme === "dark";

  // Redux hooks
  const dispatch = useDispatch();

  // Redirect to NHO Dashboard when location changes while on this touchpoint page
  const selectedLocation = useSelectedLocation();
  const initialLocationIdRef = useRef<string | undefined>(selectedLocation?.id);
  const handleBackClickRef = useRef(handleBackClick);
  useEffect(() => { handleBackClickRef.current = handleBackClick; }, [handleBackClick]);
  const hasRedirectedRef = useRef(false);
  useEffect(() => {
    const initialId = initialLocationIdRef.current;
    if (
      !hasRedirectedRef.current &&
      initialId &&
      selectedLocation?.id &&
      initialId !== selectedLocation.id
    ) {
      hasRedirectedRef.current = true;
      dispatch(resetFilters());
      if (handleBackClickRef.current) handleBackClickRef.current();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation?.id]);

  // Toast hook
  const { showToast } = useToast();

  // Success callback function for SpecificPeriodTouchPointAction
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleSuccess = (message: string) => {
    dispatch(setSuccessMessage(message));
  };

  // Access Redux state
  const { selectedFilters, touchPointSurvey } = useSelector(
    (state: any) => state.nho
  );
  const { currentForm, submissionStatus } = touchPointSurvey;

  // Local state for tracking completion status
  const [isSurveyComplete, setIsSurveyComplete] = useState<boolean>(false);
  const [surveyItems, setSurveyItems] = useState<
    Array<{
      id: string;
      sectionValue: string;
      sectionType: string;
      section: number;
    }>
  >([]);
  const [isViewOnlyMode, setIsViewOnlyMode] = useState<boolean>(false);
  const [isSurveyLoading, setIsSurveyLoading] = useState<boolean>(true);

  // Scroll to top button state
  const scrollViewRef = useRef<ScrollView>(null);
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollButtonOpacity = useRef(new Animated.Value(0)).current;

  // Clear submission status on component mount
  useEffect(() => {
    dispatch(clearTouchPointSubmissionStatus());
  }, [dispatch]);

  // Handle successful submission
  useEffect(() => {
    if (submissionStatus.success) {
      // Use success callback to show persistent notification in Dashboard instead of toast
      handleSuccess("Touchpoint data saved successfully.");

      // Auto-navigate back after showing success message
      setTimeout(() => {
        dispatch(resetTouchPointSurveyForm());
        if (handleBackClick) {
          handleBackClick();
        }
      }, 2000); // Give user time to see the success message
    }
  }, [submissionStatus.success, dispatch, handleBackClick, handleSuccess]);

  // Handle submission errors
  useEffect(() => {
    if (submissionStatus.error) {
      showToast({
        header: "Submission Failed",
        subtext: `${submissionStatus.error}. Please check your data and try again.`,
        variant: "error",
        position: "top",
      });

      // Clear the error status after showing toast
      setTimeout(() => {
        dispatch(clearTouchPointSubmissionStatus());
      }, 3500); // Clear after toast auto-hides
    }
  }, [submissionStatus.error, dispatch, showToast]);

  // Helper function to get title based on touchpoint type
  const getWelcomeTitle = () => {
    if (selectedFilters.observationType === "preferred_method") {
      return "Preferred Method Checklist";
    } else {
      // Convert legacy values to milestone display names
      const displayName = getTouchPointDisplayName(selectedFilters.observationType);
      return `Probationary Review Form - ${displayName}`;
    }
  };

  // Handle employee signature capture
  const handleEmployeeSignature = (fileName: string | null, imageUrl?: string | null) => {
    console.log("Employee signature captured:", fileName, imageUrl);
    dispatch(
      setTouchPointEmployeeSignature({
        fileName,
        imageUrl: imageUrl || null,
      })
    );
  };

  // Handle manager signature capture
  const handleManagerSignature = (fileName: string | null, imageUrl?: string | null) => {
    console.log("Manager signature captured:", fileName, imageUrl);
    dispatch(
      setTouchPointManagerSignature({
        fileName,
        imageUrl: imageUrl || null,
      })
    );
  };

  // Handle survey answers change
  const handleSurveyAnswersChange = (
    answers: Record<string, any>,
    isComplete: boolean
  ) => {
    dispatch(setTouchPointSurveyAnswers(answers));
    dispatch(setTouchPointSurveyComplete(isComplete));
    setIsSurveyComplete(isComplete);
    console.log("Survey answers updated:", answers, "Complete:", isComplete);
  };

  // Handle survey comments change
  const handleSurveyCommentsChange = (comments: Record<string, string>) => {
    dispatch(setTouchPointSurveyComments(comments));
    console.log("Survey comments updated:", comments);
  };

  // Handle survey items loaded
  const handleSurveyItemsLoaded = (
    items: Array<{
      id: string;
      sectionValue: string;
      sectionType: string;
      section: number;
    }>
  ) => {
    setSurveyItems(items);
    console.log("Survey items loaded:", items);
  };

  // Handle view-only mode change from TouchPointSurveyContainer
  const handleViewOnlyModeChange = (isViewOnly: boolean) => {
    setIsViewOnlyMode(isViewOnly);
    console.log("View-only mode changed:", isViewOnly);
  };

  // Handle loading state change from TouchPointSurveyContainer
  const handleLoadingChange = (isLoading: boolean) => {
    setIsSurveyLoading(isLoading);
    console.log("Survey loading state changed:", isLoading);
  };

  // Check if all requirements are met for completion
  const isFormComplete = () => {
    const hasEmployeeSignature = !!currentForm.employeeSignature.fileName;
    const hasManagerSignature = !!currentForm.managerSignature.fileName;
    const allRequirementsMet =
      isSurveyComplete && hasEmployeeSignature && hasManagerSignature;

    console.log("Form completion check:", {
      isSurveyComplete,
      hasEmployeeSignature,
      hasManagerSignature,
      allRequirementsMet,
    });

    return allRequirementsMet;
  };

  // Handle form submission with status
  const handleSubmit = async (action: "save" | "complete") => {
    // Debug: Log comments before building payload
    console.log("CurrentForm.comments before submit:", currentForm.comments);
    const isCompleteAction = action === "complete";

    // For complete action, validate the form
    if (isCompleteAction) {
      const isValid = isFormComplete();
      if (!isValid) {
        // Create detailed error message
        const missingItems = [];
        if (!isSurveyComplete) {
          missingItems.push("survey questions");
        }
        if (!currentForm.employeeSignature.fileName) {
          missingItems.push("employee signature");
        }
        if (!currentForm.managerSignature.fileName) {
          missingItems.push("manager signature");
        }

        const errorMessage = `Please complete the following: ${missingItems.join(
          ", "
        )}.`;

        showToast({
          header: "Incomplete Form",
          subtext: errorMessage,
          variant: "warning",
          position: "top",
        });

        return false;
      }
    }

    // Determine status based on action
    const status = isCompleteAction ? "Completed" : "In Progress";

    // Prepare the submission payload with explicit mapping for comment fields
    const submissionPayload: TouchPointSubmissionPayload = {
      surveyAnswers: currentForm.surveyAnswers,
      // Map first comment box to observations, second to additional_observations
      observations: currentForm.comments["section-1"] || "",
      additional_observations:
        currentForm.comments["section-2"] ||
        currentForm.comments["section-3"] ||
        "",
      comments: currentForm.comments,
      employeeSignature: currentForm.employeeSignature,
      managerSignature: currentForm.managerSignature,
      observationType: selectedFilters.observationType,
      employeeGpid: selectedFilters.employeeGpid,
      locationName: selectedFilters.locationName,
      employeeName: `${selectedFilters.employeeFirstName || ""} ${
        selectedFilters.employeeLastName || ""
      }`.trim(),
      surveyItems: surveyItems,
      status: status, // Add status based on action
    };

    console.log(
      `Submitting touchpoint with action: ${action}, status: ${status}`
    );

    // Dispatch the submission action
    dispatch(submitTouchPointSurvey(submissionPayload) as any);

    return true;
  };

  // Handle form completion (wrapper for backward compatibility)
  const handleComplete = async () => {
    return await handleSubmit("complete");
  };

  // Handle save for later
  const handleSaveForLater = async (): Promise<boolean> => {
    await handleSubmit("save");
    return true;
  };

  // Scroll handling
  const handleScroll = (event: any) => {
    const { contentOffset } = event.nativeEvent;
    const shouldShow = contentOffset.y > 200;

    if (shouldShow !== showScrollToTop) {
      setShowScrollToTop(shouldShow);
      Animated.timing(scrollButtonOpacity, {
        toValue: shouldShow ? 1 : 0,
        duration: 300,
        useNativeDriver: true,
      }).start();
    }
  };

  const scrollToTop = () => {
    try {
      if (Platform.OS === "web") {
        window.scrollTo({
          top: 0,
          left: 0,
          behavior: "smooth",
        });
      } else if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          x: 0,
          y: 0,
          animated: true,
        });
      }
    } catch (scrollError) {
      console.error("Error scrolling to top:", scrollError);
    }
  };

  // Handle scroll events for web
  useEffect(() => {
    if (Platform.OS === "web") {
      const handleWebScroll = () => {
        const shouldShow = window.scrollY > 200;
        
        if (shouldShow !== showScrollToTop) {
          setShowScrollToTop(shouldShow);
          
          Animated.timing(scrollButtonOpacity, {
            toValue: shouldShow ? 1 : 0,
            duration: 300,
            useNativeDriver: true,
          }).start();
        }
      };

      window.addEventListener('scroll', handleWebScroll);
      return () => window.removeEventListener('scroll', handleWebScroll);
    }
  }, [showScrollToTop, scrollButtonOpacity]);

  return (
    <View
      style={[
        styles.container,
        isDarkTheme && { backgroundColor: colors.background.page },
      ]}
      testID="specific-period-touchpoint"
    >
      <ScrollView 
        ref={scrollViewRef}
        style={styles.contentScrollView}
        onScroll={Platform.OS !== "web" ? handleScroll : undefined}
        scrollEventThrottle={16}
      >
        {/* Breadcrumb + Title (keeps original title below) */}
        <View style={styles.headerContainer}>
          <DLOBreadcrumb
            items={[
              {
                id: 'nho',
                label: 'NHO',
                onPress: () => {
                  // reset touchpoint form state and navigate back
                  dispatch(resetTouchPointSurveyForm());
                  if (handleBackClick) handleBackClick();
                },
              },
              {
                id: 'touchpoint',
                label: getWelcomeTitle(),
                isCurrentPage: true,
              },
            ]}
            showHomeIcon={true}
            onHomePress={onHomePress}
          />

          <View style={styles.titleContainer}>
            <Text style={styles.title}>{getWelcomeTitle()}</Text>
          </View>
        </View>

        {/* Welcome Card */}
        {/* <TouchPointWelcomeCard
          title={getWelcomeTitle()}
          onBackClick={handleBackClick} // Use the combined back handler
          testID="touchpoint-welcome-card"
        /> */}

        {/* Content area */}
        <View
          style={[
            styles.contentArea,
            isDarkTheme && {
              backgroundColor: colors.background.surface,
              borderWidth: 1,
              borderColor: colors.border.default,
            },
          ]}
        >
          {/* Survey component */}
          <TouchPointSurveyContainer
            observationType={selectedFilters.observationType}
            onAnswersChange={handleSurveyAnswersChange}
            onCommentsChange={handleSurveyCommentsChange}
            onSurveyItemsLoaded={handleSurveyItemsLoaded}
            onViewOnlyModeChange={handleViewOnlyModeChange}
            onLoadingChange={handleLoadingChange}
            testID="specific-period-survey"
          />
        </View>

        {/* Signature section with dual signature pads - Hidden when survey is loading */}
        {!isSurveyLoading && (
          <SignatureSection
            employeeSignatureFileName={currentForm.employeeSignature.fileName}
            employeeSignatureImageUrl={currentForm.employeeSignature.imageUrl}
            managerSignatureFileName={currentForm.managerSignature.fileName}
            managerSignatureImageUrl={currentForm.managerSignature.imageUrl}
            handleEmployeeSignature={handleEmployeeSignature}
            handleManagerSignature={handleManagerSignature}
            isViewOnlyMode={isViewOnlyMode}
          />
        )}

        {/* Action buttons (Save/Complete/PDF Export) - Hidden when survey is loading */}
        {!isSurveyLoading && (
          <View
            style={[
              styles.actionContainer,
              isDarkTheme && {
                backgroundColor: colors.background.surface,
                borderWidth: 1,
                borderColor: colors.border.default,
              },
            ]}
          >
            <SpecificPeriodTouchPointAction
              isFormComplete={isFormComplete()}
              isSubmitting={submissionStatus.isSubmitting}
              onComplete={handleComplete}
              onSaveForLater={handleSaveForLater}
              onSuccess={handleSuccess}
              onBackToDashboard={handleBackClick}
              selectedTouchPoint={{
                type: selectedFilters.observationType,
                employeeSignature: currentForm.employeeSignature.fileName,
                managerSignature: currentForm.managerSignature.fileName,
              }}
              periodType={selectedFilters.observationType}
              isViewOnlyMode={isViewOnlyMode}
            />
          </View>
        )}
      </ScrollView>

      {/* Scroll to Top Button */}
      {showScrollToTop && (
        <Animated.View
          style={[
            styles.scrollToTopButton,
            {
              opacity: scrollButtonOpacity,
              transform: [
                {
                  scale: scrollButtonOpacity.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.8, 1],
                  }),
                },
              ],
            },
          ]}
          pointerEvents={showScrollToTop ? "auto" : "none"}
        >
          <TouchableOpacity
            style={[
              styles.scrollToTopTouchable,
              isDarkTheme && {
                backgroundColor: colors.background.elevated,
                borderColor: colors.border.default,
              },
            ]}
            onPress={scrollToTop}
            activeOpacity={0.7}
          >
            <View style={styles.arrowIcon}>
              <Text
                style={[
                  styles.arrowText,
                  isDarkTheme && { color: colors.text.primary },
                ]}
              >
                ↑
              </Text>
            </View>
          </TouchableOpacity>
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F5F9FF",
    padding: Platform.OS === "web" ? 20 : 16,
  },
  contentScrollView: {
    flex: 1,
   // padding: Platform.OS === "web" ? 20 : 16,
  },
  contentArea: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 0,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  contentText: {
    fontSize: 16,
    color: "#2D2D2D",
  },
  actionContainer: {
    backgroundColor: "white",
    borderRadius: 8,
    padding: 16,
    marginBottom: 16,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  scrollToTopButton: {
    position: "absolute",
    bottom: Platform.OS === "web" ? 30 : 80,
    right: 20,
    zIndex: 1000,
  },
  scrollToTopTouchable: {
    backgroundColor: "#FFFFFF",
    borderRadius: 28,
    width: 56,
    height: 56,
    justifyContent: "center",
    alignItems: "center",
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 4,
    },
    shadowOpacity: 0.3,
    shadowRadius: 4.65,
    elevation: 8,
    borderWidth: 1,
    borderColor: "#E0E0E0",
  },
  arrowIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  arrowText: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#0066CC",
    lineHeight: 24,
  },
  headerContainer: {
    width: "100%",
    paddingBottom: 16,
    backgroundColor: "transparent",
  },
  titleContainer: {
    paddingVertical: 8,
  },
  title: {
    fontSize: Platform.OS === "web" ? 28 : isMobileIos ? 18 : 24,
    fontWeight: "700",
    color: "#2D2D2D",
  },
});

export default SpecificPeriodTouchPoint;
